/**
 * 制作工作台 · 剧情面板 · 1:1 复刻编剧侧 S 场景视图
 *
 * 数据来自 plan.scenes[] 透传的 writer 字段(location/action/dialogues/cameras),
 * 渲染规则与 apps/web/src/pages/writer/DialogueWorkspace.tsx:199-259 保持一致:
 * - 紫色左边框 + 斜体 OS 块(type === 'inner_monologue' 或 emotion 暗示心声)
 * - 灰色 △ 环境/动作镜头(type === 'action' 或文本被双括号包裹)
 * - 普通 speaker emotion 对白块
 */
import type { DirectorSceneShots } from './types';

interface Props {
  scene: DirectorSceneShots;
}

export function SceneScriptCard({ scene }: Props) {
  const dialogues = scene.dialogues ?? [];
  const cameras = scene.cameras ?? [];
  const hasAnything =
    Boolean(scene.location?.trim()) ||
    Boolean(scene.action?.trim()) ||
    dialogues.length > 0 ||
    cameras.length > 0;
  if (!hasAnything) return null;

  const totalDuration = cameras.reduce((sum, c) => sum + (c.durationSec ?? 0), 0);
  const sceneDurationSec = totalDuration > 0 ? totalDuration : null;

  return (
    <article className="rounded-lg border border-outline-variant/40 bg-surface-variant/20 px-4 py-3 shadow-sm">
      <header className="flex items-center justify-between text-xs text-outline">
        <span className="rounded bg-brand-50 px-1.5 py-0.5 font-medium text-brand-700">
          剧情面板 · {scene.sceneId}
        </span>
        <div className="flex items-center gap-2">
          {sceneDurationSec != null && (
            <span className="rounded bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
              推荐视频时长 {sceneDurationSec}s
            </span>
          )}
          {scene.location && (
            <span className="text-neutral-500">{scene.location}</span>
          )}
        </div>
      </header>

      {scene.action && (
        <p className="mt-2 whitespace-pre-line text-xs leading-relaxed text-neutral-500">
          {scene.action}
        </p>
      )}

      {dialogues.length > 0 && (
        <ul className="mt-2 space-y-1">
          {dialogues.map((d, i) => {
            const emo = (d.emotion || '').toLowerCase();
            const innerEmo =
              emo.includes('内心') ||
              emo.includes('独白') ||
              emo.includes('自言') ||
              emo === 'os' ||
              emo.includes('心声');
            const isInner = d.type === 'inner_monologue' || innerEmo;
            const text = typeof d.text === 'string' ? d.text : '';
            const isAction =
              d.type === 'action' || /^\s*[((].*[))]\s*$/.test(text);
            if (isAction) {
              return (
                <li
                  key={i}
                  className="text-xs italic leading-relaxed text-neutral-500"
                >
                  <span className="mr-1.5 text-neutral-400">·</span>
                  {d.speaker && (
                    <span className="text-neutral-500">{d.speaker}</span>
                  )}
                  <span className="ml-1">{text}</span>
                </li>
              );
            }
            return (
              <li
                key={i}
                className={
                  isInner
                    ? 'rounded-md border-l-2 border-violet-300 bg-violet-50/60 px-2 py-1 text-sm italic leading-relaxed text-violet-900'
                    : 'text-sm leading-relaxed text-neutral-800'
                }
              >
                <strong
                  className={
                    isInner
                      ? 'mr-1.5 text-violet-700'
                      : 'mr-1.5 text-brand-700'
                  }
                >
                  {d.speaker}
                  {isInner ? ' · OS' : ''}:
                </strong>
                {d.emotion && (
                  <span
                    className={
                      isInner
                        ? 'mr-1 text-[10px] text-violet-500'
                        : 'mr-1 text-[10px] text-neutral-400'
                    }
                  >
                    [{d.emotion}]
                  </span>
                )}
                {text}
              </li>
            );
          })}
        </ul>
      )}

      {cameras.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5 border-t border-outline-variant/30 pt-2 text-[11px] text-neutral-500">
          <span className="text-neutral-400">编剧侧镜头预设:</span>
          {cameras.map((c, i) => (
            <span
              key={i}
              className="rounded bg-neutral-100 px-1.5 py-0.5 text-neutral-600"
            >
              {[c.shot, c.move, c.durationSec ? `${c.durationSec}s` : null]
                .filter(Boolean)
                .join(' · ')}
            </span>
          ))}
        </div>
      )}
    </article>
  );
}
